import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { UserIcon, MapPinIcon, CalendarIcon, PlusIcon, PackageIcon, BriefcaseIcon, CalendarDaysIcon, NewspaperIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { WalletDisplay } from '../components/WalletDisplay';
import { CreateListingForm } from '../components/CreateListingForm';
import { ListingCard } from '../components/ListingCard';
import { ListingType, LISTING_TYPE_LABELS } from '../types';
export function ProfilePage() {
  const {
    user,
    isAuthenticated,
    userListings,
    deleteListing,
    addFunds
  } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState<ListingType | 'all'>('all');
  const [showAddFunds, setShowAddFunds] = useState(false);
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace data-id="element-524" />;
  }
  const filteredListings = activeTab === 'all' ? userListings : userListings.filter(l => l.type === activeTab);
  const tabs: {
    key: ListingType | 'all';
    label: string;
    icon: typeof PackageIcon;
  }[] = [{
    key: 'all',
    label: 'All',
    icon: PackageIcon
  }, {
    key: 'good',
    label: 'Goods',
    icon: PackageIcon
  }, {
    key: 'service',
    label: 'Services',
    icon: BriefcaseIcon
  }, {
    key: 'event',
    label: 'Events',
    icon: CalendarDaysIcon
  }, {
    key: 'news',
    label: 'News',
    icon: NewspaperIcon
  }];
  const handleAddFunds = () => {
    addFunds(100);
    setShowAddFunds(false);
  };
  return <main className="min-h-screen bg-gray-50" data-id="element-525">
      {/* Profile Header */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-12 px-4" data-id="element-526">
        <div className="max-w-7xl mx-auto" data-id="element-527">
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6" data-id="element-528">
            <img src={user.avatar} alt={user.name} className="w-24 h-24 rounded-full border-4 border-white shadow-lg" data-id="element-529" />
            <div className="text-center md:text-left" data-id="element-530">
              <h1 className="text-3xl font-bold" data-id="element-531">{user.name}</h1>
              <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mt-2 text-blue-100" data-id="element-532">
                <div className="flex items-center justify-center md:justify-start space-x-1" data-id="element-533">
                  <UserIcon className="w-4 h-4" data-id="element-534" />
                  <span data-id="element-535">{user.email}</span>
                </div>
                <div className="flex items-center justify-center md:justify-start space-x-1" data-id="element-536">
                  <MapPinIcon className="w-4 h-4" data-id="element-537" />
                  <span data-id="element-538">{user.location}</span>
                </div>
                <div className="flex items-center justify-center md:justify-start space-x-1" data-id="element-539">
                  <CalendarIcon className="w-4 h-4" data-id="element-540" />
                  <span data-id="element-541">
                    Joined {new Date(user.joinedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-542">
        <div className="grid lg:grid-cols-3 gap-8" data-id="element-543">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6" data-id="element-544">
            {/* Wallet */}
            <WalletDisplay balance={user.walletBalance} onAddFunds={() => setShowAddFunds(true)} data-id="element-545" />

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-md p-6" data-id="element-546">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-547">Your Stats</h3>
              <div className="space-y-3" data-id="element-548">
                <div className="flex justify-between" data-id="element-549">
                  <span className="text-gray-600" data-id="element-550">Total Listings</span>
                  <span className="font-semibold" data-id="element-551">{userListings.length}</span>
                </div>
                <div className="flex justify-between" data-id="element-552">
                  <span className="text-gray-600" data-id="element-553">Total Views</span>
                  <span className="font-semibold" data-id="element-554">
                    {userListings.reduce((acc, l) => acc + l.views, 0)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-555">
                  <span className="text-gray-600" data-id="element-556">Active Goods</span>
                  <span className="font-semibold" data-id="element-557">
                    {userListings.filter(l => l.type === 'good').length}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-558">
                  <span className="text-gray-600" data-id="element-559">Active Services</span>
                  <span className="font-semibold" data-id="element-560">
                    {userListings.filter(l => l.type === 'service').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Create Listing Button */}
            <button onClick={() => setShowCreateForm(true)} className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 shadow-md" data-id="element-561">
              <PlusIcon className="w-5 h-5" data-id="element-562" />
              <span data-id="element-563">Create New Listing</span>
            </button>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2" data-id="element-564">
            <div className="bg-white rounded-xl shadow-md" data-id="element-565">
              {/* Tabs */}
              <div className="border-b" data-id="element-566">
                <div className="flex overflow-x-auto" data-id="element-567">
                  {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-568">
                      <tab.icon className="w-4 h-4" data-id="element-569" />
                      <span data-id="element-570">{tab.label}</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs" data-id="element-571">
                        {tab.key === 'all' ? userListings.length : userListings.filter(l => l.type === tab.key).length}
                      </span>
                    </button>)}
                </div>
              </div>

              {/* Listings */}
              <div className="p-6" data-id="element-572">
                {filteredListings.length > 0 ? <div className="grid sm:grid-cols-2 gap-4" data-id="element-573">
                    {filteredListings.map(listing => <ListingCard key={listing.id} listing={listing} showActions onDelete={deleteListing} data-id="element-574" />)}
                  </div> : <div className="text-center py-12" data-id="element-575">
                    <PackageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-576" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-577">
                      No listings yet
                    </h3>
                    <p className="text-gray-500 mb-4" data-id="element-578">
                      {activeTab === 'all' ? "You haven't created any listings yet." : `You don't have any ${LISTING_TYPE_LABELS[activeTab as ListingType].toLowerCase()} listings.`}
                    </p>
                    <button onClick={() => setShowCreateForm(true)} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-579">
                      <PlusIcon className="w-5 h-5" data-id="element-580" />
                      <span data-id="element-581">Create Your First Listing</span>
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Create Listing Modal */}
      {showCreateForm && <CreateListingForm onClose={() => setShowCreateForm(false)} onSuccess={() => {}} data-id="element-582" />}

      {/* Add Funds Modal */}
      {showAddFunds && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-583">
          <div className="bg-white rounded-xl max-w-sm w-full p-6" data-id="element-584">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-585">Add Funds</h3>
            <p className="text-gray-600 mb-6" data-id="element-586">
              This is a demo. Click below to add 100 KES to your wallet.
            </p>
            <div className="flex space-x-3" data-id="element-587">
              <button onClick={() => setShowAddFunds(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors" data-id="element-588">
                Cancel
              </button>
              <button onClick={handleAddFunds} className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-589">
                Add 100 KES
              </button>
            </div>
          </div>
        </div>}
    </main>;
}